import { use, type ReactNode } from 'react';
import type { DemoData } from '@pointfive/contracts';
import { apiBinding, client, DataContext } from './DataProvider';

/** Data for the two surfaces a person can reach without an account (WP-02, SDD §7.4).
 *
 *  What this changes, precisely: the public card and the investor portal no longer establish a session, and no
 *  longer wait on a fetch that started at import time. Before this, loading `/card/:token` performed a dev-login
 *  where one was configured and requested the whole authenticated aggregate before any route had been matched — a
 *  public visitor's first act was to ask for a private dataset.
 *
 *  What this does **not** yet change, and it should not be read as more than it is: where a session cookie happens
 *  to exist, these surfaces still render from the trader's own aggregate rather than from a grant-scoped read. The
 *  `public.card.get` and `portal.views.*` procedures exist and are simulated (0 of 6 live), and pointing these pages
 *  at them is B6 of the procedure conversion, not a wrapper's job. The isolation is real; the scoping is not there
 *  yet, and `audit/INVENTORY.md` says so by name.
 *
 *  Without a session the read fails, which is correct and is now visible: an explicit unavailable state rather than
 *  a crash, an empty frame, or — worst — somebody else's figures.
 */

type Public = { data: DemoData } | { data: null; reason: 'unavailable' };

let promise: Promise<Public> | null = null;

function load(): Promise<Public> {
  promise ??= (async (): Promise<Public> => {
    try {
      return { data: await client.demo.dataset() };
    } catch {
      // Unauthenticated, expired, revoked, or the API is down. From out here they are one thing: this link does not
      // currently show anything, and saying so is the whole job.
      return { data: null, reason: 'unavailable' };
    }
  })();
  return promise;
}

export function resetPublicData(): void {
  promise = null;
}

export function PublicDataProvider({ children }: { children: ReactNode }) {
  const result = use(load());
  if (!result.data) return <Unavailable />;
  return <DataContext.Provider value={result.data}>{children}</DataContext.Provider>;
}

/** Deliberately plain, and deliberately not an error page. A link that has expired or been revoked is an ordinary
 *  thing to encounter, and the person holding it is usually not the person who can fix it. */
function Unavailable() {
  return (
    <div
      data-page="public-unavailable"
      data-theme="dark"
      style={{
        minHeight: '100vh',
        background: 'var(--pub-ground)',
        color: 'var(--pub-ink)',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 16px',
      }}
    >
      <div style={{ maxWidth: '420px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontSize: '13.5px', fontWeight: 700, letterSpacing: '-0.01em' }}>PointFive OS</div>
        <div style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '-0.01em' }}>
          This link is not available
        </div>
        <div style={{ fontSize: '11.5px', color: 'var(--pub-dim)', lineHeight: 1.6 }}>
          It may have expired, been revoked by its owner, or never have been valid. Ask whoever shared it for
          a current one.
        </div>
      </div>
    </div>
  );
}

/** Which binding these surfaces are reading through, for the badge the pages already render. */
export const publicBinding = apiBinding;
